import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Trash2, Shield, Package, Anchor, Calendar, Ruler, Fuel, Droplets, Settings } from 'lucide-react';
import { ArticleInventaire } from '@/hooks/useInventaire';
import { Bateau } from '@/hooks/useBateaux';
import { EspaceStockage } from '@/hooks/useEspacesStockage';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { fr as frLocale, enGB } from 'date-fns/locale';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';

interface ArticleEquipement {
  id: string;
  nom: string;
  categorie: string;
  sous_categorie: string | null;
  quantiteActuelle: number;
  quantiteMax: number;
  unite: string;
  datePeremption?: string | null;
}

interface ListeEquipementSecuriteProps {
  articles: ArticleInventaire[];
  onClose: () => void;
  bateau?: Bateau;
  espacesStockage?: EspaceStockage[];
}

const ListeEquipementSecurite = ({ articles, onClose, bateau, espacesStockage = [] }: ListeEquipementSecuriteProps) => {
  const { t } = useTranslation('inventory');
  // Filtrer uniquement les articles d'équipements et sécurité
  const articlesEquipementSecurite = articles.filter(article => {
    const cat = article.categorie.toLowerCase();
    return cat.includes('équip') || cat.includes('sécur') || cat.includes('equip') || cat.includes('secur') || 
           cat === 'équipements' || cat === 'sécurité' || cat === 'equipements' || cat === 'securite' ||
           cat === 'équipement' || cat === 'équipement de sécurité' || cat === 'matériel de sécurité' ||
           cat === 'equipement' || cat === 'equipement de securite' || cat === 'materiel de securite';
  });

  const [listeArticles, setListeArticles] = useState<ArticleEquipement[]>(
    articlesEquipementSecurite.map(article => ({
      id: article.id,
      nom: article.nom,
      categorie: article.categorie,
      sous_categorie: article.sous_categorie,
      quantiteActuelle: article.quantite,
      quantiteMax: article.quantite_max,
      unite: article.unite,
      datePeremption: article.date_peremption
    }))
  );

  const supprimerArticle = (id: string) => {
    setListeArticles(prev => prev.filter(article => article.id !== id));
  };

  const exporterListe = () => {
    const dateLocale = i18n.language?.startsWith('en') ? enGB : frLocale;
    const dateGeneration = format(new Date(), 'dd/MM/yyyy', { locale: dateLocale });
    const equipements = listeArticles.filter(a => {
      const cat = a.categorie.toLowerCase();
      return cat.includes('équip') || cat.includes('equip');
    });
    const securite = listeArticles.filter(a => {
      const cat = a.categorie.toLowerCase();
      return cat.includes('sécur') || cat.includes('secur');
    });
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPosition = 15;

    // En-tête avec couleurs du site
    doc.setFillColor(31, 41, 55); // Bleu nautic
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    // Logo/Nom du site
    doc.setTextColor(218, 165, 32); // Or nautic
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(t('pdf.brand'), 20, 20);
    
    // Titre principal
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text(t('pdf.safetyEquipmentTitleAlt'), pageWidth / 2, 28, { align: 'center' });
    
    yPosition = 50;
    
    // Informations du bateau
    if (bateau) {
      doc.setTextColor(31, 41, 55);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(t('pdf.boatInfoTitle'), 20, yPosition);
      
      // Ligne de séparation
      doc.setDrawColor(218, 165, 32);
      doc.setLineWidth(1);
      doc.line(20, yPosition + 2, pageWidth - 20, yPosition + 2);
      yPosition += 15;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      // Section Identification
      doc.setFont(undefined, 'bold');
      doc.text(t('pdf.identification'), 20, yPosition);
      yPosition += 6;
      doc.setFont(undefined, 'normal');
      doc.text(`• ${t('pdf.name')}: ${bateau.nom}`, 25, yPosition);
      yPosition += 5;
      doc.text(`• ${t('pdf.manufacturer')}: ${bateau.nom_constructeur}`, 25, yPosition);
      yPosition += 5;
      doc.text(`• ${t('pdf.model')}: ${bateau.nom_modele}`, 25, yPosition);
      yPosition += 5;
      doc.text(`• ${t('pdf.year')}: ${bateau.annee}`, 25, yPosition);
      yPosition += 8;
      
      // Section Caractéristiques
      doc.setFont(undefined, 'bold');
      doc.text(t('pdf.characteristics'), 20, yPosition);
      yPosition += 6;
      doc.setFont(undefined, 'normal');
      doc.text(`• ${t('pdf.length')}: ${bateau.longueur}m`, 25, yPosition);
      yPosition += 5;
      doc.text(`• ${t('pdf.width')}: ${bateau.largeur}m`, 25, yPosition);
      yPosition += 5;
      if (bateau.tirant_eau) {
        doc.text(`• ${t('pdf.draft')}: ${bateau.tirant_eau}m`, 25, yPosition);
        yPosition += 5;
      }
      doc.text(`• ${t('pdf.engine')}: ${bateau.moteur}`, 25, yPosition);
      yPosition += 5;
      if (bateau.heures_moteur) {
        doc.text(`• ${t('pdf.engineHours')}: ${bateau.heures_moteur}h`, 25, yPosition);
        yPosition += 5;
      }
      yPosition += 3;
      
      // Section Capacités
      doc.setFont(undefined, 'bold');
      doc.text(t('pdf.capacities'), 20, yPosition);
      yPosition += 6;
      doc.setFont(undefined, 'normal');
      doc.text(`• ${t('pdf.fuel')}: ${bateau.capacite_carburant}L`, 25, yPosition);
      yPosition += 5;
      doc.text(`• ${t('pdf.freshWater')}: ${bateau.capacite_eau}L`, 25, yPosition);
      yPosition += 5;
      if (bateau.capacite_eaux_usees) {
        doc.text(`• ${t('pdf.wasteWater')}: ${bateau.capacite_eaux_usees}L`, 25, yPosition);
        yPosition += 5;
      }
      
      // Section Immatriculation
      if (bateau.immatriculation || bateau.port_attache) {
        yPosition += 3;
        doc.setFont(undefined, 'bold');
        doc.text(t('pdf.registrationSection'), 20, yPosition);
        yPosition += 6;
        doc.setFont(undefined, 'normal');
        if (bateau.immatriculation) {
          doc.text(`• ${t('pdf.registration')}: ${bateau.immatriculation}`, 25, yPosition);
          yPosition += 5;
        }
        if (bateau.port_attache) {
          doc.text(`• ${t('pdf.homePort')}: ${bateau.port_attache}`, 25, yPosition);
          yPosition += 5;
        }
      }
      
      yPosition += 10;
    }
    
    doc.setTextColor(31, 41, 55);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${t('pdf.generatedOn')}: ${dateGeneration}`, 20, yPosition);
    
    yPosition += 20;

    // Résumé avec design amélioré
    doc.setFillColor(218, 165, 32); // Or nautic
    doc.rect(15, yPosition - 5, pageWidth - 30, 25, 'F');
    
    doc.setTextColor(31, 41, 55);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(t('pdf.summary'), 20, yPosition + 5);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text(`${t('pdf.totalArticles')}: ${listeArticles.length}`, 20, yPosition + 12);
    doc.text(`${t('pdf.equipment')}: ${equipements.length}`, 100, yPosition + 12);
    doc.text(`${t('pdf.security')}: ${securite.length}`, 20, yPosition + 18);
    
    yPosition += 35;

    // Section Équipements
    if (equipements.length > 0) {
      doc.setTextColor(31, 41, 55);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(t('pdf.equipmentSection'), 20, yPosition);
      
      // Ligne de séparation
      doc.setDrawColor(218, 165, 32);
      doc.setLineWidth(2);
      doc.line(20, yPosition + 2, pageWidth - 20, yPosition + 2);
      yPosition += 15;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      equipements.forEach((article) => {
        if (yPosition > 260) {
          doc.addPage();
          yPosition = 20;
        }
        
        const articleOriginal = articles.find(a => a.id === article.id);
        const stockColor = article.quantiteActuelle === 0 ? [255, 0, 0] : 
                          article.quantiteActuelle / article.quantiteMax <= 0.2 ? [255, 165, 0] : [0, 128, 0];
        
        doc.text(`- ${article.nom}`, 25, yPosition);
        yPosition += 5;
        
        if (articleOriginal?.description) {
          doc.text(`  ${t('pdf.description')}: ${articleOriginal.description}`, 30, yPosition);
          yPosition += 5;
        }
        
        if (article.sous_categorie) {
          doc.text(`  ${t('pdf.category')}: ${article.sous_categorie}`, 30, yPosition);
          yPosition += 5;
        }
        
        doc.setTextColor(stockColor[0], stockColor[1], stockColor[2]);
        doc.text(`  ${t('pdf.stock')}: ${article.quantiteActuelle}/${article.quantiteMax} ${article.unite}`, 30, yPosition);
        doc.setTextColor(0, 0, 0);
        yPosition += 5;
        
        if (articleOriginal?.prix_unitaire && articleOriginal.prix_unitaire > 0) {
          doc.text(`  ${t('pdf.unitPrice')}: ${articleOriginal.prix_unitaire.toFixed(2)} EUR`, 30, yPosition);
          yPosition += 5;
        }
        
        if (articleOriginal?.espace_stockage_id) {
          const emplacement = espacesStockage.find(e => e.id === articleOriginal.espace_stockage_id);
          if (emplacement) {
            doc.text(`  ${t('pdf.location')}: ${emplacement.nom} (${emplacement.type})`, 30, yPosition);
            yPosition += 5;
          }
        }
        
        yPosition += 3;
      });
      
      yPosition += 10;
    }

    // Section Sécurité
    if (securite.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setTextColor(31, 41, 55);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(t('pdf.securitySection'), 20, yPosition);
      
      // Ligne de séparation
      doc.setDrawColor(218, 165, 32);
      doc.setLineWidth(2);
      doc.line(20, yPosition + 2, pageWidth - 20, yPosition + 2);
      yPosition += 15;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      securite.forEach((article) => {
        if (yPosition > 260) {
          doc.addPage();
          yPosition = 20;
        }
        
        const articleOriginal = articles.find(a => a.id === article.id);
        const stockColor = article.quantiteActuelle === 0 ? [255, 0, 0] : 
                          article.quantiteActuelle / article.quantiteMax <= 0.2 ? [255, 165, 0] : [0, 128, 0];
        
        doc.text(`- ${article.nom}`, 25, yPosition);
        yPosition += 5;
        
        if (articleOriginal?.description) {
          doc.text(`  ${t('pdf.description')}: ${articleOriginal.description}`, 30, yPosition);
          yPosition += 5;
        }
        
        if (article.sous_categorie) {
          doc.text(`  ${t('pdf.category')}: ${article.sous_categorie}`, 30, yPosition);
          yPosition += 5;
        }
        
        doc.setTextColor(stockColor[0], stockColor[1], stockColor[2]);
        doc.text(`  ${t('pdf.stock')}: ${article.quantiteActuelle}/${article.quantiteMax} ${article.unite}`, 30, yPosition);
        doc.setTextColor(0, 0, 0);
        yPosition += 5;
        
        if (articleOriginal?.prix_unitaire && articleOriginal.prix_unitaire > 0) {
          doc.text(`  ${t('pdf.unitPrice')}: ${articleOriginal.prix_unitaire.toFixed(2)} EUR`, 30, yPosition);
          yPosition += 5;
        }
        
        if (article.datePeremption) {
          doc.setTextColor(255, 165, 0);
          doc.text(`  ${t('pdf.expires')}: ${format(new Date(article.datePeremption), 'dd/MM/yyyy', { locale: dateLocale })}`, 30, yPosition);
          doc.setTextColor(0, 0, 0);
          yPosition += 5;
        }
        
        if (articleOriginal?.espace_stockage_id) {
          const emplacement = espacesStockage.find(e => e.id === articleOriginal.espace_stockage_id);
          if (emplacement) {
            doc.text(`  ${t('pdf.location')}: ${emplacement.nom} (${emplacement.type})`, 30, yPosition);
            yPosition += 5;
          }
        }
        
        yPosition += 3;
      });
    }

    // Télécharger le PDF
    doc.save(`equipements-securite-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (listeArticles.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-nautic">
            <Shield className="h-5 w-5 text-nautic" />
            {t('listeEquipementSecurite.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">
            {t('listeEquipementSecurite.noEquipmentFound')}
          </p>
          <Button onClick={onClose} className="mt-4 bg-nautic-gold text-nautic hover:bg-nautic-lightgold">
            {t('listeEquipementSecurite.close')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const equipements = listeArticles.filter(a => {
    const cat = a.categorie.toLowerCase();
    return cat.includes('équip') || cat.includes('equip');
  });
  const securite = listeArticles.filter(a => {
    const cat = a.categorie.toLowerCase();
    return cat.includes('sécur') || cat.includes('secur');
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-nautic">
          <Shield className="h-5 w-5 text-nautic" />
          {t('listeEquipementSecurite.titleWithCount', { count: listeArticles.length })}
        </CardTitle>
        <div className="flex gap-2">
          <Button onClick={exporterListe} variant="outline" size="sm" className="border-nautic text-nautic hover:bg-nautic-light hover:text-white">
            <Download className="h-4 w-4 mr-2" />
            {t('listeEquipementSecurite.exportPdf')}
          </Button>
          <Button onClick={onClose} variant="outline" size="sm">
            {t('listeEquipementSecurite.close')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Informations du bateau */}
        {bateau && (
          <div className="mb-6 p-4 bg-gradient-to-r from-nautic-background to-nautic-light/10 rounded-lg border border-nautic-light/20">
            <div className="flex items-center gap-2 mb-4">
              <Anchor className="h-5 w-5 text-nautic" />
              <h3 className="text-lg font-semibold text-nautic">{t('listeEquipementSecurite.boatInfo')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Informations principales */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-nautic border-b border-nautic-light/30 pb-1">{t('listeEquipementSecurite.identification')}</h4>
                <div className="space-y-1 text-sm">
                  <div><span className="font-medium">{t('listeEquipementSecurite.name')}</span> {bateau.nom}</div>
                  <div><span className="font-medium">{t('listeEquipementSecurite.manufacturer')}</span> {bateau.nom_constructeur}</div>
                  <div><span className="font-medium">{t('listeEquipementSecurite.model')}</span> {bateau.nom_modele}</div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span className="font-medium">{t('listeEquipementSecurite.year')}</span> {bateau.annee}
                  </div>
                </div>
              </div>

              {/* Dimensions et motorisation */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-nautic border-b border-nautic-light/30 pb-1">{t('listeEquipementSecurite.characteristics')}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-1">
                    <Ruler className="h-3 w-3" />
                    <span className="font-medium">{t('listeEquipementSecurite.length')}</span> {bateau.longueur}m
                  </div>
                  <div className="flex items-center gap-1">
                    <Ruler className="h-3 w-3" />
                    <span className="font-medium">{t('listeEquipementSecurite.width')}</span> {bateau.largeur}m
                  </div>
                  {bateau.tirant_eau && (
                    <div className="flex items-center gap-1">
                      <Ruler className="h-3 w-3" />
                      <span className="font-medium">{t('listeEquipementSecurite.draft')}</span> {bateau.tirant_eau}m
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Settings className="h-3 w-3" />
                    <span className="font-medium">{t('listeEquipementSecurite.engine')}</span> {bateau.moteur}
                  </div>
                  {bateau.heures_moteur && (
                    <div><span className="font-medium">{t('listeEquipementSecurite.engineHours')}</span> {bateau.heures_moteur}h</div>
                  )}
                </div>
              </div>

              {/* Capacités et immatriculation */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-nautic border-b border-nautic-light/30 pb-1">{t('listeEquipementSecurite.capacitiesAndRegistration')}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-1">
                    <Fuel className="h-3 w-3" />
                    <span className="font-medium">{t('listeEquipementSecurite.fuel')}</span> {bateau.capacite_carburant}L
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplets className="h-3 w-3" />
                    <span className="font-medium">{t('listeEquipementSecurite.freshWater')}</span> {bateau.capacite_eau}L
                  </div>
                  {bateau.capacite_eaux_usees && (
                    <div className="flex items-center gap-1">
                      <Droplets className="h-3 w-3" />
                      <span className="font-medium">{t('listeEquipementSecurite.wasteWater')}</span> {bateau.capacite_eaux_usees}L
                    </div>
                  )}
                  {bateau.immatriculation && (
                    <div><span className="font-medium">{t('listeEquipementSecurite.registration')}</span> {bateau.immatriculation}</div>
                  )}
                  {bateau.port_attache && (
                    <div><span className="font-medium">{t('listeEquipementSecurite.homePort')}</span> {bateau.port_attache}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Résumé */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="border-nautic-light">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-nautic-light rounded-lg">
                  <Package className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('listeEquipementSecurite.equipment')}</p>
                  <p className="text-xl font-bold text-nautic">{equipements.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Shield className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('listeEquipementSecurite.security')}</p>
                  <p className="text-xl font-bold text-orange-600">{securite.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('listeEquipementSecurite.article')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.category')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.subCategory')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.stock')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.unit')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.expiration')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.location')}</TableHead>
                <TableHead>{t('listeEquipementSecurite.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listeArticles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell className="font-medium">{article.nom}</TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={
                        article.categorie.toLowerCase().includes('sécur') 
                          ? "border-orange-200 text-orange-600 bg-orange-50" 
                          : "border-nautic text-nautic bg-nautic-background"
                      }
                    >
                      {article.categorie}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {article.sous_categorie && (
                      <Badge variant="secondary">{article.sous_categorie}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={
                      article.quantiteActuelle === 0 
                        ? "text-red-600 font-semibold" 
                        : article.quantiteActuelle / article.quantiteMax <= 0.2 
                          ? "text-orange-600 font-semibold"
                          : "text-green-600 font-semibold"
                    }>
                      {article.quantiteActuelle} / {article.quantiteMax}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {article.unite}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {article.datePeremption ? (
                      <span className="text-sm text-muted-foreground">
                        {new Date(article.datePeremption).toLocaleDateString('fr-FR')}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const articleOriginal = articles.find(a => a.id === article.id);
                      if (articleOriginal?.espace_stockage_id) {
                        const emplacement = espacesStockage.find(e => e.id === articleOriginal.espace_stockage_id);
                        if (emplacement) {
                          return (
                            <Badge variant="outline" className="text-xs">
                              {emplacement.nom} ({emplacement.type})
                            </Badge>
                          );
                        }
                      }
                      return <span className="text-sm text-muted-foreground">-</span>;
                    })()}
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => supprimerArticle(article.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ListeEquipementSecurite;